import { getLogger } from '@packages/core/logging';
import {
    DeleteAsset,
    FetchActiveAssets,
    filterCaseLevelAssets,
    type TechlineAssetEntity,
    UpdateAsset,
    type UpdateAssetParams,
} from '@packages/domain/techline/assets';
import { container } from '@web/services/container';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { broadcastCaseStatusChange } from '../../utils/case-tab-sync';
import { isSupportedCaseAttachmentFile } from '../case-attachment-file-types';
import { useAssetUpload } from './use-asset-upload';

const logger = getLogger('techline');

// Operations are prefixed with $ to make them visually distinct in this file.
const [$deleteTechlineAsset, $fetchActiveTechlineAssets, $updateTechlineAsset] = container.resolveOperations(
    DeleteAsset,
    FetchActiveAssets,
    UpdateAsset
);

/**
 * Manage techline case assets: fetching, uploading, deleting, updating.
 * Upload lifecycle (progress tracking, polling for completion) is delegated
 * to useAssetUpload. This hook owns server asset state and produces a unified
 * list that merges server-complete assets with in-progress uploads.
 *
 * @param caseNumber - the case to manage assets for
 * @param caseStatusCode - the current case status (affects upload request payload)
 * @param isDraftCase - when true, filters out worksheet assets; when false, includes all assets
 * @param onAssetError - callback invoked for pre-upload validation, upload, or server-side processing failures
 * @returns asset state and mutation functions
 */
export function useAssets(
    caseNumber: string | undefined,
    caseStatusCode: string | undefined,
    isDraftCase: boolean,
    onAssetError: (filename: string, error: string) => void
) {
    const [serverAssetData, setServerAssetData] = useState<TechlineAssetEntity[]>([]);

    /**
     * Fetch all active assets for the case from the server.
     */
    const refreshServerAssets = useCallback(async () => {
        if (!caseNumber) {
            return;
        }

        try {
            const assets = await $fetchActiveTechlineAssets({ caseNumber });
            setServerAssetData(assets);
        } catch (err) {
            alert('Error: ' + err + '. Please refresh & try again.');
            logger.error(`Failed to fetch assets [${caseNumber}]`, { caseNumber, error: err });
        }
    }, [caseNumber]);

    // Hooks

    const assetUpload = useAssetUpload(refreshServerAssets, onAssetError);

    // Derived state

    /**
     * Unified asset list merging server assets with in-progress uploads.
     * In-progress entries take precedence over server entries with the same ID,
     * because in-progress entries carry the correct lifecycle status (e.g.
     * 'processing') while the server may still report 'uploading'.
     */
    const assetData: TechlineAssetEntity[] = useMemo(() => {
        // Draft cases show only case-level assets; submitted cases show all assets
        const filteredAssets = isDraftCase ? filterCaseLevelAssets(serverAssetData) : serverAssetData;
        const inProgressIds = new Set(assetUpload.inProgressAssetData.map((a) => a.id));
        const serverOnly = filteredAssets.filter((a) => !inProgressIds.has(a.id));
        return [...serverOnly, ...assetUpload.inProgressAssetData];
    }, [isDraftCase, serverAssetData, assetUpload.inProgressAssetData]);

    // Handlers

    /**
     * Upload a new file as an asset.
     *
     * @param file - the file to upload
     */
    const uploadAsset = useCallback(
        async (file: File) => {
            if (!caseNumber || !caseStatusCode) {
                return;
            }
            if (!isSupportedCaseAttachmentFile(file)) {
                onAssetError(file.name, 'Unsupported file format');
                return;
            }
            await assetUpload.uploadAsset(file, caseNumber, caseStatusCode);
            broadcastCaseStatusChange({ caseNumber });
        },
        [assetUpload, caseNumber, caseStatusCode, onAssetError]
    );

    /**
     * Soft-delete an asset, then refresh the asset list.
     *
     * @param assetId - the ID of the asset to delete
     */
    const deleteAsset = useCallback(
        async (assetId: string) => {
            await $deleteTechlineAsset({ assetId });
            await refreshServerAssets();
        },
        [refreshServerAssets]
    );

    /**
     * Update an asset's blob or metadata, then refresh the asset list.
     *
     * @param assetId - the ID of the asset to update
     * @param update - the changes to apply
     */
    const updateAsset = useCallback(
        async (assetId: string, update: UpdateAssetParams['update']) => {
            if (!caseNumber || !caseStatusCode) {
                return;
            }

            const originalAsset = serverAssetData.find((a) => a.id === assetId);
            if (!originalAsset) {
                throw new Error('Asset not found');
            }

            // Execute the update; returns the new asset ID when a blob was replaced
            const replacementAssetId = await $updateTechlineAsset({
                assetId,
                caseNumber,
                caseStatusCode,
                originalAsset,
                update,
            });

            if (replacementAssetId) {
                // Blob replacement: track the new asset in the polling loop with a preview
                // thumbnail from the edited blob, then refresh to remove the old asset
                assetUpload.trackReplacementAsset(
                    replacementAssetId,
                    originalAsset.name,
                    originalAsset.assetType,
                    update.blob
                );
            }

            // Refresh server assets to pick up changes (removal of old asset for replacements,
            // or updated metadata for non-blob updates)
            await refreshServerAssets();
            broadcastCaseStatusChange({ caseNumber });
        },
        [assetUpload, caseNumber, caseStatusCode, refreshServerAssets, serverAssetData]
    );

    // Effects

    /** Fetch assets on mount and clean up polling timers on unmount. */
    useEffect(() => {
        if (caseNumber) {
            refreshServerAssets();
        }
        return () => {
            assetUpload.clearAllPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseNumber]);

    return {
        /** Unified asset list: server-complete assets merged with in-progress uploads. */
        assetData,

        /** Delete a case-level asset by ID, then refresh the asset list. */
        deleteAsset,

        /** Fetch all active assets for the case from the server. */
        refreshAssets: refreshServerAssets,

        /** Update an asset's blob or metadata, then refresh the asset list. */
        updateAsset,

        /** Upload a new file as a case-level asset with full lifecycle tracking. */
        uploadAsset,
    };
}
